import type RootStore from "stores/RootStore";
import type { DialogType } from "types/DialogType";
import { ABANDONED, type SubmitResult } from "./InstanceUsersStore";
import type InstanceUsersStore from "./InstanceUsersStore";

/**
 * The dialogs behind Client 360's Users tab, registered on the ClientDetail
 * route (clientDetail.Route.ts spreads this list) so they get the router's
 * dialog chrome and its full-screen treatment on phones.
 */

export const INSTANCE_USER_FORM_DIALOG = "InstanceUserFormDialog";
export const INSTANCE_USER_CONFIRM_DIALOG = "InstanceUserConfirmDialog";
export const INSTANCE_USER_SECRET_DIALOG = "InstanceUserSecretDialog";
export const INSTANCE_CONNECTION_DIALOG = "InstanceConnectionDialog";

const storeOf = (rootStore: RootStore) =>
  rootStore.instanceUsersViewStore as unknown as InstanceUsersStore;

const openDialog = (rootStore: RootStore, dialogName: string) =>
  rootStore.routerStore?.openDialog?.({ dialogName });

/**
 * A failed write THROWS: the dialog framework keeps a dialog open when its
 * onSubmit rejects and shows the message in the footer, so the error stays
 * visible with the form intact. On success, run the follow-up (e.g. the
 * one-time password) a tick later, because the framework's own close runs
 * after onSubmit resolves and would close a dialog opened synchronously here.
 * Same shape as API Keys'.
 */
const afterSubmit = async (
  rootStore: RootStore,
  submit: (store: InstanceUsersStore) => Promise<SubmitResult>,
  errorOf: (store: InstanceUsersStore) => string | null,
  fallback: string,
  onSuccess?: (store: InstanceUsersStore) => void,
) => {
  const store = storeOf(rootStore);
  const result = await submit(store);
  // The page moved to another client while this was in flight: let the dialog
  // close and do nothing else — keeping it open, or chaining a follow-up, would
  // put this client's form in front of the other one.
  if (result === ABANDONED) return;
  if (!result) throw new Error(errorOf(store) || fallback);
  if (onSuccess) setTimeout(() => onSuccess(store), 0);
};

/** Show the one-time password if the last write produced one. */
const revealSecretIfAny = (rootStore: RootStore) => (store: InstanceUsersStore) => {
  if (store.secret) openDialog(rootStore, INSTANCE_USER_SECRET_DIALOG);
};

export const instanceUsersDialogs: DialogType[] = [
  {
    name: INSTANCE_USER_FORM_DIALOG,
    title: "Instance user",
    component: () => import("./dialogs/InstanceUserForm.Body"),
    cancelButton: true,
    maxWidth: "sm",
    height: 560,
    submitButtonText: "Save",
    onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
      afterSubmit(
        rootStore,
        (s) => s.saveUser(),
        (s) => s.formError,
        "The instance user could not be saved.",
        revealSecretIfAny(rootStore),
      ),
    onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) =>
      storeOf(rootStore).isUserFormValid && !storeOf(rootStore).saving,
  },
  {
    // Disable / reset password. Failures land on the tab's banner rather than
    // re-opening: there is nothing in this dialog to correct.
    name: INSTANCE_USER_CONFIRM_DIALOG,
    title: "Confirm",
    component: () => import("./dialogs/InstanceUserConfirm.Body"),
    cancelButton: true,
    maxWidth: "xs",
    height: 260,
    submitButtonText: "Confirm",
    onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
      const store = storeOf(rootStore);
      const ok = await store.runConfirmed();
      if (ok) setTimeout(() => revealSecretIfAny(rootStore)(store), 0);
    },
    onClose: ({ rootStore }: { rootStore: RootStore }) => storeOf(rootStore).cancelConfirm(),
  },
  {
    // The temporary password, once. No cancel: dismissing it IS acknowledging
    // it, and a "Cancel" would suggest it could be fetched again. It cannot —
    // HQ never stored it.
    name: INSTANCE_USER_SECRET_DIALOG,
    title: "Temporary password",
    component: () => import("./dialogs/InstanceUserSecret.Body"),
    cancelButton: false,
    maxWidth: "sm",
    height: 380,
    submitButtonText: "I've passed it on",
    onSubmit: ({ rootStore }: { rootStore: RootStore }) => storeOf(rootStore).clearSecret(),
    onClose: ({ rootStore }: { rootStore: RootStore }) => storeOf(rootStore).clearSecret(),
  },
  {
    // Admin: the instance's address, app name and service user, plus issuing
    // and checking the key. Saving a NEW connection re-opens it so the key can
    // be issued straight away.
    name: INSTANCE_CONNECTION_DIALOG,
    title: "Instance connection",
    component: () => import("./dialogs/InstanceConnectionForm.Body"),
    cancelButton: true,
    maxWidth: "md",
    height: 640,
    submitButtonText: "Save",
    onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
      const store = storeOf(rootStore);
      const wasNew = !store.editingConnectionId;
      return afterSubmit(
        rootStore,
        (s) => s.saveConnection(),
        (s) => s.connectionError,
        "The connection could not be saved.",
        () => {
          if (wasNew) openDialog(rootStore, INSTANCE_CONNECTION_DIALOG);
        },
      );
    },
    onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) =>
      storeOf(rootStore).isConnectionFormValid && !storeOf(rootStore).connectionSaving,
    // Nothing secret outlives the dialog: the issued key's config entry, nor an
    // inquiry publishing key typed and not saved.
    onClose: ({ rootStore }: { rootStore: RootStore }) => {
      const store = storeOf(rootStore);
      store.clearIssuedKey();
      store.clearInquiryKeyDraft();
    },
  },
];
